import { useState } from 'react';
import { isAdmin } from '../utils/entitlements';
import { fmtWhen, isBusy, requestUpdate, useUpdateStatus } from '../utils/updateStatus';

// Auto-update notices on every page.
//  - an update is installing        → full-screen notice (detection is paused)
//  - a soft update is available     → blue strip, "Update now" for admins, "Later" hides it 24 h
//  - a required update is scheduled → amber strip with the install time
//  - just updated                   → green strip, dismissable
//  - an update failed and was undone→ admins only, with the reason
const LATER_KEY = 'prvsk_update_later';
const DISMISS_KEY = 'prvsk_update_dismissed';

function readLS(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}
function writeLS(key, v) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private window */ }
}

const STEP_TEXT = {
  installing:   'Installing the update…',
  health_check: 'Checking the new version…',
  rolling_back: 'Something went wrong — restoring the previous version…',
};

const strip = (bg, fg, border) => ({
  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0,
  padding: '7px 16px', fontSize: 13, lineHeight: 1.4,
  background: bg, color: fg, borderBottom: `1px solid ${border}`,
});
const btn = (primary) => ({
  border: primary ? 'none' : '1px solid currentColor', borderRadius: 6, padding: '3px 10px',
  fontSize: 12, fontWeight: 600, cursor: 'pointer',
  background: primary ? '#1d4ed8' : 'transparent', color: primary ? '#fff' : 'inherit',
});

export default function UpdateBanner() {
  const [st] = useUpdateStatus();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [, force] = useState(0);
  const admin = isAdmin();

  if (!st || st.state === 'not_installed') return null;

  if (isBusy(st)) {
    return (
      <div role="alert" style={{
        position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: 'rgba(15, 23, 42, 0.72)', padding: 16,
      }}>
        <div style={{ background: '#fff', color: '#0f172a', borderRadius: 12, padding: '28px 32px',
                      maxWidth: 440, textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,.25)' }}>
          <div className="adm-spinner" style={{ margin: '0 auto 16px' }} />
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>System is being updated</div>
          <div style={{ fontSize: 14, color: '#475569' }}>
            {STEP_TEXT[st.state] || STEP_TEXT.installing}
            <br />Detection resumes in about 2 minutes. This page reconnects on its own.
          </div>
        </div>
      </div>
    );
  }

  const avail = st.available;
  const build = avail?.build_number;

  const apply = async () => {
    setBusy(true); setErr('');
    try { await requestUpdate('apply', build); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (st.state === 'updated' && st.last_result) {
    const key = `${st.current_build}`;
    if (readLS(DISMISS_KEY) === key) return null;
    return (
      <div role="status" style={strip('#ecfdf3', '#065f46', '#a7f3d0')}>
        ✓ Updated to {st.current_version || `build ${st.current_build}`}.
        <button style={btn(false)} onClick={() => { writeLS(DISMISS_KEY, key); force(n => n + 1); }}>Dismiss</button>
      </div>
    );
  }

  if ((st.state === 'rolled_back' || st.state === 'blocked') && admin && st.last_result) {
    const r = st.last_result;
    return (
      <div role="status" style={strip('#fef2f2', '#7f1d1d', '#fecaca')}>
        ⚠ Update to build {r.build} failed and was undone{r.reason ? ` (${r.reason})` : ''}.
        {st.state === 'blocked' ? ' It will not be retried; support has been notified.' : ' It will be retried automatically.'}
      </div>
    );
  }

  if (!avail || !['ready', 'scheduled', 'downloading', 'rolled_back'].includes(st.state)) return null;

  if (st.mandatory) {
    return (
      <div role="status" style={strip('#fff8eb', '#7a4700', '#f0d49b')}>
        ⬆ A required update ({avail.version || `build ${build}`}) will install automatically
        {st.scheduled_for ? ` at ${fmtWhen(st.scheduled_for)}` : ' soon'}. Detection pauses for about 2 minutes.
        {admin && st.state !== 'downloading' && (
          <button style={btn(true)} disabled={busy} onClick={apply}>{busy ? 'Starting…' : 'Install now'}</button>
        )}
        {err && <span style={{ color: '#b91c1c' }}>{err}</span>}
      </div>
    );
  }

  const later = readLS(LATER_KEY);
  if (later && later.build === build && Date.now() < later.until) return null;

  return (
    <div role="status" style={strip('#eff6ff', '#1e3a8a', '#bfdbfe')}>
      ⬆ Update {avail.version || `build ${build}`} is available{avail.notes ? ` — ${avail.notes}` : ''}.
      {st.state === 'downloading' && ' Downloading…'}
      {admin && st.state !== 'downloading' && (
        <button style={btn(true)} disabled={busy} onClick={apply}>{busy ? 'Starting…' : 'Update now'}</button>
      )}
      <button style={btn(false)} onClick={() => {
        writeLS(LATER_KEY, { build, until: Date.now() + 24 * 3600 * 1000 }); force(n => n + 1);
      }}>Later</button>
      {err && <span style={{ color: '#b91c1c' }}>{err}</span>}
    </div>
  );
}
